import { ChevronRightIcon, XIcon } from '@langchain/untitled-ui-icons';

import * as React from 'react';

import { Tooltip } from '@/design-system';
import { cn, utils } from '@/utils/tailwind';

import { useExpandCollapse } from './expandCollapseContext';

export const SimpleCard = (props: {
  title: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  open?: boolean;
  required?: boolean;
  hideRequiredLabel?: boolean;
  onDeleteKey?: () => void;
}) => {
  const expand = useExpandCollapse();
  const [_open, _setOpen] = React.useState(
    expand?.globalExpandState ?? props.open
  );
  const id = React.useId();

  React.useEffect(() => {
    expand?.updateChildState(id, !!open);
    return () => {
      expand?.unregister(id);
    };
  }, []);

  const open = React.useMemo(() => {
    return expand?.expansionStates.get(id) ?? _open;
  }, [expand, id, _open]);

  const handleClick = () => {
    if (expand) {
      expand.updateChildState(id, !open);
    } else {
      _setOpen((prev) => !prev);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col items-stretch rounded-lg',
        open && 'mb-4 last-of-type:mb-0',
        props.className
      )}
    >
      <button
        type="button"
        className="-mx-2 grid grid-cols-[1fr,auto] gap-4 rounded px-2 py-2 transition-colors hover:bg-secondary-hover active:bg-secondary"
        onClick={handleClick}
      >
        <span className="flex items-center gap-2">{props.title}</span>

        <span className="flex items-center gap-2">
          {props.onDeleteKey && !props.required && (
            <Tooltip title="Delete key">
              <button
                type="button"
                className={cn(
                  utils.button,
                  'rounded-md border border-secondary'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  props.onDeleteKey?.();
                }}
              >
                <XIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
          {!props.hideRequiredLabel && (
            <span className="rounded-md border border-secondary px-1 py-0.5 text-sm text-tertiary">
              {props.required ? 'Required' : 'Optional'}
            </span>
          )}

          <ChevronRightIcon
            className={cn(
              'h-4 w-4 text-primary transition-transform',
              open && 'rotate-90'
            )}
          />
        </span>
      </button>
      {open && <div className="flex flex-col gap-2">{props.children}</div>}
    </div>
  );
};
